import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { AppButton, AppModal, AppSpin, AppTable } from '@/components/atoms';
import { WEEKDAY_LABELS_BY_INDEX } from '@/constants/weekday';
import { formatJapaneseDate } from '@/utils/dateFormat';
import type { VisitScheduleDayDetail, VisitScheduleDetailRow } from '@/services/main/visitSchedule';
import { VISIT_DISTANCE_THRESHOLD_KM } from '@/constants/visit';

function getDistanceNumber(distance: unknown): number | null {
  if (distance == null || distance === '') return null;
  const value = Number(distance);
  return Number.isFinite(value) ? value : null;
}

const columns: ColumnsType<VisitScheduleDetailRow> = [
  {
    title: '番号',
    dataIndex: 'number',
    key: 'number',
    width: 60,
    align: 'center',
  },
  {
    title: '拠点（出発元）',
    key: 'from',
    render: (_, row) => (
      <>
        <div>{row.from_address || '—'}</div>
        <div className="mt-1 text-gray-600">{row.from_name || '—'}</div>
      </>
    ),
  },
  {
    title: '訪問先',
    key: 'to',
    render: (_, row) => {
      // const hasLatLng = row.to_latitude != null && row.to_longitude != null;
      // const mapHref = hasLatLng
      //   ? `https://maps.google.com/maps?q=${row.to_latitude},${row.to_longitude}`
      //   : null;
      return (
        <>
          <div>
            {row.to_full_address || row.to_address || '—'}
            {/* {mapHref && (
              <a
                href={mapHref}
                target="_blank"
                rel="noreferrer"
                className="ml-1 inline-flex items-center gap-0.5 text-blue-600"
              >
                <i className="fa fa-map-marker" aria-hidden="true" />
                [地図]
              </a>
            )} */}
          </div>
          <div className="mt-1 text-gray-600">{row.to_name || '—'}</div>
        </>
      );
    },
  },
  {
    title: '距離(Km)',
    key: 'distance',
    width: 120,
    render: (_, row) => {
      const distance = getDistanceNumber(row.distance);
      return distance == null || distance < 0 ? (
        <span className="text-red-600">距離測定不能</span>
      ) : (
        <span
          className={
            distance > VISIT_DISTANCE_THRESHOLD_KM && !row.is_non_charge
              ? 'text-red-600 line-through'
              : ''
          }
        >
          {row.distance}
        </span>
      );
    },
  },
];

interface VisitScheduleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  detail: VisitScheduleDayDetail | null;
  clinicLocationId?: number | null;
  isLoading?: boolean;
}

export function VisitScheduleDetailModal({
  isOpen,
  onClose,
  detail,
  clinicLocationId,
  isLoading = false,
}: VisitScheduleDetailModalProps) {
  const { tenant } = useParams<{ tenant: string }>();
  const dateLabel = detail?.visitdate
    ? `${formatJapaneseDate(detail.visitdate)}(${WEEKDAY_LABELS_BY_INDEX[dayjs(detail.visitdate).day()]})`
    : '—';
  const hasVisits = !!detail && detail.rows.length > 0;

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="訪問詳細"
      maxWidth="max-w-5xl"
      hideCancelButton
      footer={null}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-800">{dateLabel}</div>
          <div className="flex items-center gap-2">
            {hasVisits && detail?.visitdate && detail?.massager?.id && clinicLocationId && (
              <AppButton
                type="primary"
                className="!w-[120px]"
                onClick={() => {
                  window.open(
                    `/${tenant}/visits/distance_change/${detail.visitdate}/${detail.massager.id}/${clinicLocationId}`,
                    '_blank'
                  );
                }}
              >
                距離編集
              </AppButton>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <AppSpin size="large" />
          </div>
        ) : !hasVisits ? null : (
          <AppTable<VisitScheduleDetailRow>
            dataSource={detail!.rows}
            rowKey="visit_id"
            pagination={false}
            rowClassName={row =>
              row.is_locked ? '!bg-gray-100' : row.is_error ? '!bg-[rgb(255,240,245)]' : ''
            }
            columns={columns}
          />
        )}
      </div>
    </AppModal>
  );
}
